import { useState } from "react";
import { Card, Icon } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout } from "ui-components/Layout";
import { EditButton } from "ui-components/IconButton";
import { Success, Error_ } from "ui-components/Alert";
import { DelButton } from "ui-components/IconButton";
import BasicTabs from "ui-components/BasicTabs";
import { MessageDialog } from "ui-components/Alert";
import { ActiveButton } from "ui-components/IconButton";
import { TableCard } from "ui-components/Layout";
import { useTeamValue } from "providers/TeamContext";
import { useCustomerValue } from "providers/CustomerContext";
import { deactivateLocationGroup } from "services/data.internal.service";
import { activeLocationGroup } from "services/data.internal.service";
import { getLocationGroupStatusCount } from "services/data.internal.service";
import { getLocationGroupList } from "services/data.internal.service";

function LocationGroupList() {
  const {permissions, allowAll} = useTeamValue();
  const {customerCode} = useCustomerValue()
  const navigate = useNavigate();
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [status, setStatus] = useState("Active");
  const active = status === "Active" ? true : false;
  const [tabs, setTabs] = useState([
    { label: "Active", count: 0 },
    { label: "InActive", count: 0 },
  ]);
  const [data, setData] = useState([]);
  const queryClient = useQueryClient();
  const onSuccess = (data) => {
    queryClient.invalidateQueries(["location-group-list"]);
    setAlert(<Success data={data} onChange={setAlert} />);
  };
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);

  const addPermission = allowAll || (Boolean(permissions["Location Group"]) && permissions["Location Group"].findIndex(b => b==="new") !== -1);
  const editPermission = allowAll || (Boolean(permissions["Location Group"]) && permissions["Location Group"].findIndex(b => b==="edit") !== -1);
  const deactivatePermission = allowAll || (Boolean(permissions["Location Group"]) && permissions["Location Group"].findIndex(b => b==="deactivate") !== -1);

  useQuery({
    queryKey: ["location-group-list", { customerCode, active }],
    queryFn: () => getLocationGroupList({ active }),
    onSuccess: (data) => setData(data.data.data),
    onError,
  });

  useQuery({
    queryKey: ["location-group-list", "status", {customerCode}],
    queryFn: () => getLocationGroupStatusCount(),
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });

  const delMutation = useMutation(deactivateLocationGroup, { onSuccess, onError });
  const activeMutation = useMutation(activeLocationGroup, { onSuccess, onError });

  const onEdit = (req) => navigate("/setup/location-group/edit", { state: req });

  const onDelete = (req) => {
    setDialog(
      <MessageDialog
        title={`Deactivate ${req.locDimGrp}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          delMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const onActive = (req) => {
    setDialog(
      <MessageDialog
        title={`Activate ${req.locDimGrp}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          activeMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const columns = [
    { header: "Loc Dim Grp", accessorKey: "locDimGrp" },
    { header: "Loc Group Type", accessorKey: "locGrpType" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { locDimGrp } = row.original;
        const state = { locDimGrp };
        if (active) {
          return (
            <>
              <EditButton title="edit location group" disabled={!editPermission} onClick={() => onEdit(state)} />
              <DelButton title="deactivate location group" disabled={!deactivatePermission} onClick={() => onDelete(state)} />
            </>
          );
        } else {
          return <ActiveButton title="activate location group" disabled={!editPermission} onClick={() => onActive(state)} />;
        }
      },
    },
  ];
  return (
    <ListLayout>
      {dialog}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <TableCard>
        <MDBox display="flex" justifyContent="flex-start" alignItems="flex-start" margin="10px">
          <MDButton variant="gradient" color="info" disabled={!addPermission} onClick={() => navigate("/setup/location-group/add")}>
            <Icon>add</Icon>&nbsp;location&nbsp;group
          </MDButton>
        </MDBox>
        <BasicTable columns={columns} data={data} />
      </TableCard>
    </ListLayout>
  );
}

export default LocationGroupList;
